import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Divider,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Slide,
  IconButton,
  Chip
} from '@mui/material';
import {
  Security as SecurityIcon,
  Close as CloseIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Visibility as ViewIcon,
  VisibilityOff as NotViewedIcon
} from '@mui/icons-material';
import DOMPurify from 'dompurify';
import api, { handleApiError } from '../utils/api';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const KvkkApprovalModal = ({
  open,
  onClose,
  onApproved,
  context = 'both', // 'registration', 'profile_update', or 'both'
  userId = null, // For registration flow when not yet logged in
  sessionToken = null, // For registration flow before user creation
  title = null
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();

  // Determine modal title based on context
  const modalTitle = title || (context === 'registration' ? t('kvkk:headers.registrationTitle') : t('kvkk:headers.modalTitle'));

  const [configurations, setConfigurations] = useState([]);
  const [checkboxStates, setCheckboxStates] = useState({});
  const [viewedContent, setViewedContent] = useState({}); // Track which required configs have been viewed
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);
  const [contentModal, setContentModal] = useState({ open: false, config: null });

  // Fetch KVKK configurations
  const fetchConfigurations = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await api.get('/kvkk/configurations', {
        params: { context }
      });
      
      if (response.data.success) {
        const configs = response.data.data;
        setConfigurations(configs);
        
        // Initialize checkbox states and viewed content tracking
        // Preserve existing user choices and only initialize new configurations
        setCheckboxStates(prevStates => {
          const newStates = { ...prevStates };
          configs.forEach(config => {
            if (!(config._id in newStates)) {
              newStates[config._id] = false;
            }
          });
          return newStates;
        });

        setViewedContent(prevViewed => {
          const newViewed = { ...prevViewed };
          configs.forEach(config => {
            if (!(config._id in newViewed)) {
              newViewed[config._id] = false;
            }
          });
          return newViewed;
        });
      }
    } catch (error) {
      console.error('Error fetching KVKK configurations:', error);
      setError(t('kvkk:validation.submissionError'));
    } finally {
      setLoading(false);
    }
  }, [context, t]);

  useEffect(() => {
    if (open) {
      fetchConfigurations();
    }
  }, [open, fetchConfigurations]);

  const handleCheckboxChange = (configId, checked) => {
    // Find the config to check if it's required
    const config = configurations.find(c => c._id === configId);
    
    // If it's required and not viewed, don't allow checking
    if (config?.required && !viewedContent[configId] && checked) {
      setError(t('kvkk:messages.viewContentFirst'));
      return;
    }
    
    setCheckboxStates(prev => ({
      ...prev,
      [configId]: checked
    }));
    
    // Clear validation errors when user interacts
    setValidationErrors([]);
    setError('');
  };

  const validateForm = () => {
    const errors = [];
    
    const requiredConfigurations = configurations.filter(config => config.required);
    
    requiredConfigurations.forEach(config => {
      if (!viewedContent[config._id]) {
        errors.push(t('kvkk:validation.mustViewContent', { title: config.title }));
      } else if (!checkboxStates[config._id]) {
        errors.push(t('kvkk:validation.mustAccept', { title: config.title }));
      }
    });
    
    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    
    setSubmitting(true);
    setError('');
    
    try {
      // Prepare checkbox responses
      const checkboxResponses = configurations.map(config => ({
        configurationId: config._id,
        accepted: checkboxStates[config._id] || false
      }));
      
      // Determine approval type based on context
      let approvalType;
      if (context === 'registration') {
        approvalType = 'registration';
      } else if (context === 'profile_update') {
        approvalType = 'profile_update';
      } else {
        // Default to registration for 'both' context
        approvalType = 'registration';
      }
      
      const requestData = {
        approvalType,
        checkboxResponses
      };
      
      // Add userId if provided (for registration flow)
      if (userId) {
        requestData.userId = userId;
      }
      
      // Add sessionToken if provided (for registration flow before user creation)
      if (sessionToken) {
        requestData.sessionToken = sessionToken;
      }
      
      const response = await api.post('/kvkk/approval', requestData);
      
      if (response.data.success) {
        // Call success callback
        if (onApproved) {
          onApproved({
            approvalId: response.data.data.approvalId,
            approvalType: response.data.data.approvalType,
            approvedAt: response.data.data.approvedAt,
            acceptedCount: Object.values(checkboxStates).filter(Boolean).length,
            totalCount: configurations.length
          });
        }
        
        // Close modal
        onClose();
      }
    } catch (error) {
      console.error('Error submitting KVKK approval:', error);
      
      if (error.response?.data?.details?.unacceptedRequired) {
        setError(t('kvkk:messages.unacceptedRequired', { approvals: error.response.data.details.unacceptedRequired.join(', ') }));
      } else {
        setError(handleApiError(error));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleContentModalOpen = (config) => {
    setContentModal({ open: true, config });
  };

  const handleContentModalClose = () => {
    // Mark content as viewed when modal is closed
    if (contentModal.config) {
      setViewedContent(prev => ({
        ...prev,
        [contentModal.config._id]: true
      }));
    }
    setContentModal({ open: false, config: null });
  };

  const sanitizeHtml = (html) => {
    return {
      __html: DOMPurify.sanitize(html, {
        ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
        ALLOWED_ATTR: ['class']
      })
    };
  };

  const getApprovalSummary = () => {
    const acceptedCount = Object.values(checkboxStates).filter(Boolean).length;
    const requiredCount = configurations.filter(config => config.required).length;
    const acceptedRequiredCount = configurations
      .filter(config => config.required && checkboxStates[config._id])
      .length;
    const viewedRequiredCount = configurations
      .filter(config => config.required && viewedContent[config._id])
      .length;
    
    return {
      acceptedCount,
      totalCount: configurations.length,
      requiredCount,
      acceptedRequiredCount,
      viewedRequiredCount,
      canProceed: acceptedRequiredCount === requiredCount && viewedRequiredCount === requiredCount
    };
  };

  const summary = getApprovalSummary();

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 2,
            maxHeight: '90vh'
          }
        }}
      >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
          borderBottom: 1,
          borderColor: 'divider'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SecurityIcon color="primary" />
          <Typography variant={isMobile ? 'h6' : 'h5'} component="div" sx={{ fontWeight: 600 }}>
            {modalTitle}
          </Typography>
        </Box>
        
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ ml: 1 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ py: 2 }}>
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              {t('kvkk:loading.kvkkConfigsLoading')}
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {validationErrors.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              {t('kvkk:messages.requiredApprovals')}
            </Typography>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              {validationErrors.map((error, index) => (
                <li key={index}>
                  <Typography variant="body2">{error}</Typography>
                </li>
              ))}
            </ul>
          </Alert>
        )}

        {!loading && configurations.length === 0 && (
          <Alert severity="info">
            {t('kvkk:messages.noConfigurations')}
          </Alert>
        )}

        {!loading && configurations.length > 0 && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('kvkk:messages.legalNotice')}
            </Typography>

            <Box sx={{ mb: 2 }}>
              {configurations.map((config, index) => (
                <Card 
                  key={config._id}
                  variant="outlined"
                  sx={{ 
                    mb: 2,
                    border: config.required ? 2 : 1,
                    borderColor: config.required 
                      ? (checkboxStates[config._id] ? 'success.main' : 'warning.main')
                      : 'divider'
                  }}
                >
                  <CardContent sx={{ pb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {config.title}
                          </Typography>
                          {config.required && (
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: 'white', 
                                fontWeight: 600,
                                bgcolor: 'warning.main',
                                px: 1,
                                py: 0.25,
                                borderRadius: 1
                              }}
                            >
                              {t('kvkk:labels.required')}
                            </Typography>
                          )}
                        </Box>
                        
                        {/* Content Preview */}
                        <Box 
                          sx={{ 
                            mb: 2,
                            p: 2,
                            borderRadius: 1,
                            border: 2,
                            borderColor: config.required && !viewedContent[config._id] ? 'warning.main' : 'divider',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                              bgcolor: 'grey.50',
                              borderColor: 'primary.main'
                            }
                          }}
                          onClick={() => handleContentModalOpen(config)}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {config.required && !viewedContent[config._id] ? (
                              <NotViewedIcon color="warning" />
                            ) : viewedContent[config._id] ? (
                              <ViewIcon color="success" />
                            ) : (
                              <ViewIcon color="primary" />
                            )}
                            <Typography 
                              variant="body2" 
                              color={config.required && !viewedContent[config._id] ? 'warning.dark' : 'primary.main'} 
                              sx={{ fontWeight: 500 }}
                            >
                              {config.required && !viewedContent[config._id] 
                                ? t('kvkk:contentActions.viewRequired')
                                : viewedContent[config._id] 
                                  ? t('kvkk:contentActions.contentViewed')
                                  : t('kvkk:contentActions.clickToView')}
                            </Typography>
                          </Box>
                        </Box>
                        
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={checkboxStates[config._id] || false}
                              onChange={(e) => handleCheckboxChange(config._id, e.target.checked)}
                              color="primary"
                              disabled={config.required && !viewedContent[config._id]}
                            />
                          }
                          label={
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontWeight: 500,
                                color: config.required && !viewedContent[config._id] ? 'text.disabled' : 'text.primary'
                              }}
                            >
                              {config.required && !viewedContent[config._id] 
                                ? t('kvkk:labels.viewContentFirst')
                                : t('kvkk:labels.acceptApproval')}
                            </Typography>
                          }
                        />
                      </Box>
                      
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', ml: 1 }}>
                        {checkboxStates[config._id] ? (
                          <CheckIcon color="success" />
                        ) : config.required && !viewedContent[config._id] ? (
                          <ErrorIcon color="error" />
                        ) : config.required ? (
                          <ErrorIcon color="warning" />
                        ) : (
                          <InfoIcon color="action" />
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>

            {/* Summary */}
            <Card variant="outlined" sx={{ bgcolor: 'background.default' }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  {t('kvkk:labels.approvalSummary')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('kvkk:summary.totalAccepted', { acceptedCount: summary.acceptedCount, totalCount: summary.totalCount })}
                  {summary.requiredCount > 0 && (
                    <>{t('kvkk:summary.requiredStatus', { requiredCount: summary.requiredCount, viewedRequiredCount: summary.viewedRequiredCount, acceptedRequiredCount: summary.acceptedRequiredCount })}</>
                  )}
                </Typography>
                
                {!summary.canProceed && (
                  <Typography variant="body2" color="error.main" sx={{ mt: 1, fontWeight: 500 }}>
                    {summary.viewedRequiredCount < summary.requiredCount && 
                      t('kvkk:summary.viewAllRequired')}
                    {summary.acceptedRequiredCount < summary.requiredCount && 
                      t('kvkk:summary.acceptAllRequired')}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </DialogContent>

      <Divider />
      
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          color="inherit"
          disabled={submitting}
        >
          {t('kvkk:buttons.cancel')}
        </Button>
        
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || submitting || !summary.canProceed}
          startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : null}
          sx={{ minWidth: 120 }}
        >
          {submitting ? t('kvkk:buttons.saving') : t('kvkk:buttons.approveAndContinue')}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Content Modal */}
    <Dialog
      open={contentModal.open}
      onClose={handleContentModalClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
          borderBottom: 1,
          borderColor: 'divider'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoIcon color="primary" />
          <Typography variant={isMobile ? 'h6' : 'h5'} component="div" sx={{ fontWeight: 600 }}>
            {contentModal.config?.title}
          </Typography>
        </Box>
        
        <IconButton
          onClick={handleContentModalClose}
          size="small"
          sx={{ ml: 1 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ py: 2 }}>
        {contentModal.config && (
          <>
            <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
              {contentModal.config.required && (
                <Chip
                  label={t('kvkk:labels.required')}
                  color="warning"
                  size="small"
                  sx={{
                    bgcolor: 'warning.main',
                    color: 'white'
                  }}
                />
              )}
              <Chip
                label={contentModal.config.contentType === 'pdf' ? t('kvkk:labels.contentTypePdf') : t('kvkk:labels.contentTypeHtml')}
                color="secondary"
                size="small"
              />
            </Box>

            {contentModal.config.contentType === 'pdf' ? (
              <Box
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  height: isMobile ? 400 : 600,
                  overflow: 'hidden'
                }}
              >
                <iframe
                  src={`${process.env.REACT_APP_API_URL?.replace('/api', '')}${contentModal.config.pdfUrl}#toolbar=0`}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none'
                  }}
                  title={contentModal.config.title}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  '& p': { mb: 1 },
                  '& ul, & ol': { pl: 2, mb: 1 },
                  '& li': { mb: 0.5 },
                  fontSize: '0.875rem',
                  lineHeight: 1.6,
                  wordWrap: 'break-word',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  maxWidth: '100%'
                }}
                dangerouslySetInnerHTML={sanitizeHtml(contentModal.config.content)}
              />
            )}
          </>
        )}
      </DialogContent>

      <Divider />
      
      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={handleContentModalClose}
          variant="contained"
        >
          {t('kvkk:buttons.close')}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default KvkkApprovalModal;